/**
 * DynamicCatalogService — Catalogue dynamique via AWS Lambda proxy
 * 
 * Architecture :
 * App → Lambda /catalog-proxy → WooCommerce API Cashmere + iQueens AJAX
 * Cache AsyncStorage 24h côté app
 * Fallback sur produits statiques si réseau indisponible
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AWS_CONFIG } from '../config/aws_config';
import { fetchLiveCashmereProducts } from './CashmereService';
import { getCJCashmereProducts, getCJIQueensProducts } from './CJProductsService';
import { getCatalog } from './DatabaseService';

// ═══ v1.8.11 : v49 = catalogue élargi, inclut TOUS depuis Lambda DB ═══
// ═══ v1.8.15 : v50 = TOUS avec ai_metadata trending injecté ═══
const CACHE_KEY = 'outfit_dyn_v54'; // v1.12.222 : bump - 125 articles migres Vestes->Manteaux
const CACHE_TTL = 2 * 60 * 60 * 1000; // 2h (ajustable)
const API       = AWS_CONFIG.apiUrl;

// ═══ Préférences du jour : tri intelligent ═══
// Si l'user a coché des catégories ou un budget dans le popup de cold start,
// on remonte les articles correspondants en tête de liste (sans supprimer
// les autres pour ne pas vider le catalogue).
function applyDailyPrefsSort(items, prefs) {
  if (!Array.isArray(items) || items.length === 0) return items;
  if (!prefs || (!prefs.categories?.length && !prefs.budget)) return items;

  // Bornes de prix par budget
  const BUDGET_RANGES = {
    'budget':    { min: 0,    max: 50   },
    'mid-range': { min: 50,   max: 150  },
    'premium':   { min: 150,  max: 500  },
    'luxury':    { min: 500,  max: 99999},
  };
  const range = prefs.budget ? BUDGET_RANGES[prefs.budget] : null;

  // Calculer un "score d'affinité" par article et trier décroissant
  const scored = items.map(item => {
    let score = 0;

    // Catégorie demandée → +10
    if (prefs.categories?.length > 0 && prefs.categories.includes(item.category)) {
      score += 10;
    }

    // Prix dans la fourchette → +6, juste à côté +3
    if (range) {
      const price = parseFloat(item.price) || parseFloat(item.salePrice) || 0;
      if (price > 0) {
        if (price >= range.min && price <= range.max) {
          score += 6;
        } else {
          // Distance à la fourchette (plus c'est loin, moins on aime)
          const dist = price < range.min
            ? (range.min - price) / range.min
            : (price - range.max) / range.max;
          score += Math.max(0, 3 - dist * 3);
        }
      }
    }

    return { item, score };
  });

  // Tri stable : score décroissant, sinon ordre original préservé
  scored.sort((a, b) => b.score - a.score);
  return scored.map(s => s.item);
}

// Nettoyer automatiquement les anciennes versions du cache au premier appel
let _oldCacheCleaned = false;
async function cleanOldCaches() {
  if (_oldCacheCleaned) return;
  _oldCacheCleaned = true;
  try {
    const allKeys = await AsyncStorage.getAllKeys();
    const oldKeys = allKeys.filter(k =>
      k.startsWith('outfit_dyn_') && !k.startsWith(CACHE_KEY)
    );
    if (oldKeys.length > 0) {
      console.log(`[DYNAMIC] Cleaning ${oldKeys.length} old cache entries:`, oldKeys);
      await AsyncStorage.multiRemove(oldKeys);
    }
  } catch {}
}

// ─── Cache AsyncStorage ───────────────────────────────────────
async function getCached(key) {
  await cleanOldCaches(); // auto-clean au 1er appel
  try {
    const raw = await AsyncStorage.getItem(key);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return null;
    return data;
  } catch { return null; }
}

// v1.13.30 — Stale-while-revalidate : retourne les données cachées MÊME périmées,
// avec un flag `fresh`. Permet d'afficher le catalogue instantanément (cache, même
// vieux) et de rafraîchir en arrière-plan, au lieu d'attendre le scraping live.
async function getCachedStale(key) {
  try {
    const raw = await AsyncStorage.getItem(key);
    if (!raw) return { data: null, fresh: false };
    const { ts, data } = JSON.parse(raw);
    return { data: data || null, fresh: (Date.now() - ts) <= CACHE_TTL };
  } catch { return { data: null, fresh: false }; }
}

async function setCache(key, data) {
  try {
    await AsyncStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {}
}

export async function invalidateCatalogCache() {
  const keys = ['F','M'].flatMap(g => [`${CACHE_KEY}_${g}`]);
  await AsyncStorage.multiRemove(keys);
}

// ═══ v1.12.33 : Détection d'écart catalogue serveur ↔ cache ═══
//
// Permet de savoir si la DB a de nouveaux items vs ce qui est en cache,
// pour proposer un auto-refresh ou afficher un badge "nouveau".
//
// Usage :
//   const counts = await fetchCatalogCounts();
//   const stale = await isCatalogStale(gender, counts);
//   if (stale.isStale) { /* déclencher refresh */ }

const COUNTS_CACHE_KEY = 'outfit_dyn_counts_v1';

/**
 * Récupère les comptes par partenaire depuis le serveur.
 * Format de retour :
 *   { iqueens: {F, M, unisex, total}, tous: {...}, cashmere: {...} }
 */
export async function fetchCatalogCounts() {
  try {
    const res = await fetch(`${API}/catalog/counts`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data;
  } catch (e) {
    console.warn('[DYNAMIC] fetchCatalogCounts failed:', e?.message);
    return null;
  }
}

/**
 * Compare les comptes serveur avec le snapshot des items en cache (par
 * partenaire + gender). Retourne { isStale, deltas, expectedCount, actualCount }.
 *
 * Tolérance : on considère "stale" si l'écart dépasse 50 items ET 15%.
 *
 * @param {string} gender - 'F' ou 'M'
 * @param {object} serverCounts - retour de fetchCatalogCounts
 * @param {Array} cachedItems - le cache courant (cb + iq + tous fusionnés)
 * @returns {{ isStale: boolean, deltas: object, summary: string }}
 */
export function detectCatalogStaleness(gender, serverCounts, cachedItems) {
  if (!serverCounts || !Array.isArray(cachedItems)) {
    return { isStale: false, deltas: {}, summary: 'no data' };
  }
  // Compter par partenaire dans le cache
  const cachedByPartner = {};
  for (const it of cachedItems) {
    const p = it?.partner || 'unknown';
    cachedByPartner[p] = (cachedByPartner[p] || 0) + 1;
  }

  // Pour chaque partenaire serveur, comparer
  const deltas = {};
  let isStale = false;
  let totalDelta = 0;
  for (const [partner, counts] of Object.entries(serverCounts)) {
    if (partner === '_meta') continue;
    if (!counts || typeof counts !== 'object') continue;
    // Items disponibles côté serveur pour ce gender (compte F + unisex pour F, M + unisex pour M)
    const expectedForGender = (counts[gender] || 0) + (counts.unisex || 0);
    const actualInCache = cachedByPartner[partner] || 0;
    const delta = expectedForGender - actualInCache;
    deltas[partner] = {
      server: expectedForGender,
      cache: actualInCache,
      delta,
      pctDelta: expectedForGender > 0 ? (delta / expectedForGender) * 100 : 0,
    };
    totalDelta += Math.abs(delta);

    // Tolérance : > 50 items ET > 15% (le serveur dédupé v2 doit donner des
    // comptes proches du cache front. Au-delà = vrai écart à rafraîchir).
    if (delta > 50 && deltas[partner].pctDelta > 15) {
      isStale = true;
    }
  }

  return {
    isStale,
    deltas,
    summary: isStale
      ? `Stale: ${totalDelta} item(s) écart total`
      : 'Up-to-date',
  };
}

// ─── Fetch via Lambda proxy ───────────────────────────────────
async function fetchFromLambda(partner, gender, category) {
  const url = `${API}/catalog-proxy?partner=${partner}&gender=${gender}&category=${encodeURIComponent(category || 'Tout')}`;
  const res  = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    timeout: 15000,
  });
  if (!res.ok) throw new Error(`Lambda ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : (data.products || []);
}

// ─── Fetch direct WooCommerce Store API ──────────────────────
// Cashmere Boutique : /wp-json/wc/store/v1/products (public, no auth)
const CB_API  = 'https://www.cashmereboutique.com/wp-json/wc/store/v1/products';
const CB_BASE = 'https://www.cashmereboutique.com';
const CJ_PUB  = '101729219';

function cjPromo50(path) {
  const base = `https://www.kqzyfj.com/click-${CJ_PUB}-10471851`;
  if (!path) return base;
  return `${base}?url=${encodeURIComponent(path.startsWith('http') ? path : CB_BASE + path)}`;
}

function mapWooProduct(item, gender) {
  // Image : utiliser src direct cashmereboutique.com
  const imgRaw = item.images?.[0]?.src || '';
  const image  = imgRaw.replace(
    /https?:\/\/[^/]*rocketcdn[^/]*/,
    'https://www.cashmereboutique.com'
  );

  const price = parseFloat(item.prices?.sale_price || item.prices?.price || 0) / 100;
  const orig  = parseFloat(item.prices?.regular_price || 0) / 100;
  if (!price || !image || !item.name) return null;

  const disc = orig > price ? Math.round((1 - price / orig) * 100) : 50;
  const n    = (item.name || '').toLowerCase();
  // Priorité d'ordre : robes avant bas, bijoux avant accessoires
  const cat  = n.match(/dress|gown|robe|kimono|caftan/) ? 'Robes'
             : n.match(/coat|jacket|blazer|cape|poncho|cardigan/) ? 'Vestes'
             : n.match(/skirt|pant|legging|trouser|short/) ? 'Bas'
             : n.match(/necklace|bracelet|ring|earring|pendant|collier|bague/) ? 'Bijoux'
             : n.match(/bag|clutch|purse|tote|sac/) ? 'Sacs'
             : n.match(/shoe|boot|heel|sandal|sneaker|chauss/) ? 'Chaussures'
             : n.match(/scarf|shawl|pashmina|wrap|throw|blanket|glove|sock|hat|beanie|belt/) ? 'Accessoires'
             : 'Hauts';

  // URL produit réelle depuis permalink
  const permalink = item.permalink || '';
  const affiliateUrl = cjPromo50(permalink || '/');

  return {
    id:            `cb_${item.id}`,
    partner:       'cashmere',
    brand:         'Cashmere Boutique',
    gender:        gender || 'F',
    name:          item.name,
    category:      cat,
    price,
    originalPrice: orig || price * 2,
    discount:      disc,
    promoBadge:    `-${disc}%`,
    emoji:         cat === 'Vestes' ? '🧥' : cat === 'Bas' ? '👗' : cat === 'Accessoires' ? '🧣' : '🧥',
    color:         '',
    image,
    url:           affiliateUrl,
    originalUrl:   affiliateUrl,
    available:     item.is_in_stock !== false,
    rating:        4.8,
    deliveryDays:  '3-5 j',
    sizes:         cat === 'Accessoires' ? ['Unique'] : ['XS','S','M','L','XL'],
    isRealProduct: true,
  };
}

// Catégories WooCommerce Cashmere Boutique (IDs confirmés)
const CB_CATS = [
  { id: 44, gender: 'F' },  // Women's Sweaters
  { id: 52, gender: 'F' },  // Women's Robes
  { id: 46, gender: 'F' },  // Women's Coats
  { id: 49, gender: 'F' },  // Women's Dresses
  { id: 43, gender: 'F' },  // Pashminas
  { id: 38, gender: '*' },  // Scarves
  { id: 39, gender: '*' },  // Accessories
  { id: 40, gender: '*' },  // Throws & Blankets
  { id: 55, gender: 'M' },  // Men's Sweaters
  { id: 56, gender: 'M' },  // Men's Robes
  { id: 57, gender: 'M' },  // Men's Coats
];

async function fetchCashmereWoo(gender) {
  const cats = CB_CATS.filter(c => c.gender === gender || c.gender === '*');
  const all  = [];
  const seen = new Set();

  // Fetch par lots de 3 pour éviter de surcharger
  for (let i = 0; i < cats.length; i += 3) {
    const batch = cats.slice(i, i + 3);
    await Promise.all(batch.map(async ({ id, gender: g }) => {
      try {
        const url = `${CB_API}?category=${id}&per_page=100&page=1`;
        const res  = await fetch(url, {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'Outfit-App/1.0 (mobile)',
          },
        });
        if (!res.ok) return;
        const data = await res.json();
        const items = Array.isArray(data) ? data : [];
        for (const item of items) {
          const p = mapWooProduct(item, g === '*' ? gender : g);
          if (p && !seen.has(p.id)) { seen.add(p.id); all.push(p); }
        }
      } catch(e) {
        console.warn('[CB WooAPI]', id, e.message);
      }
    }));
  }
  return all;
}

// ─── iQueens — API AJAX PrestaShop ───────────────────────────
import { getIQDeepUrl, getBestPromo, applyDiscount } from './IQueensService';

const IQ_BASE   = 'https://www.iqueens.fr';
const IQ_BRANDS = ['Nike','Adidas','New Balance','Vans','Veja','Asics','Puma','Reebok',
                   'The North Face','Dr. Martens','Converse','Vagabond','Michael Kors',
                   'EA7','Birkenstock','Timberland','UGG','Steve Madden','Carhartt WIP',
                   'Calvin Klein','Tommy Hilfiger','GUESS','Samsøe Samsøe'];

function mapIQProduct(item, cat, gender) {
  const name  = item.name || item.product_name || '';
  if (!name || name.length < 3) return null;
  const brand = IQ_BRANDS.find(b => name.toLowerCase().includes(b.toLowerCase())) || 'iQueens';
  const price = parseFloat(
    item.price_amount || item.price ||
    (item.prices && (item.prices.sale_price || item.prices.price)) || 0
  );
  if (!price) return null;

  // Image — plusieurs formats possibles
  const image = item.cover?.bySize?.large_default?.url
             || item.cover?.bySize?.home_default?.url?.replace('home_default','large_default')
             || item.id_image ? `${IQ_BASE}/${item.id_product}-large_default/${item.link_rewrite || 'product'}.jpg` : null;
  if (!image) return null;

  const n = name.toLowerCase();
  const resolvedCat = n.match(/robe|dress|combinaison/) ? 'Robes'
                    : n.match(/veste|manteau|blouson|parka|blazer|cardigan/) ? 'Vestes'
                    : n.match(/jean|pantalon|legging|short|jupe|skirt/) ? 'Bas'
                    : n.match(/collier|necklace|bracelet|ring|bague|boucle|earring|pendant|chaîne|chain/) ? 'Bijoux'
                    : n.match(/sac|bag|clutch|pochette|tote|backpack/) ? 'Sacs'
                    : n.match(/chapeau|hat|casquette|lunette|ceinture|belt|foulard|écharpe|scarf|gant/) ? 'Accessoires'
                    : n.match(/t-shirt|sweat|hoodie|pull|top|chemise|shirt|blouse/) ? 'Hauts'
                    : n.match(/chauss|basket|sneaker|botte|boot|sandale|escarpin|mocassin/) ? 'Chaussures'
                    : cat || 'Hauts';

  const prodUrl = item.url || item.canonical_url || '';
  const path    = prodUrl.replace(IQ_BASE, '');
  const promo   = getBestPromo(brand);

  return {
    id:            `iq_${item.id_product || item.id}`,
    partner:       'iqueens',
    brand,
    gender,
    name,
    category:      resolvedCat,
    price:         promo ? applyDiscount(price, promo.discount) : price,
    originalPrice: price,
    discount:      promo?.discount || null,
    promoBadge:    promo?.badge || null,
    promoCode:     promo?.code || null,
    emoji:         resolvedCat === 'Chaussures' ? '👟' : resolvedCat === 'Vestes' ? '🧥' : resolvedCat === 'Bas' ? '👖' : '👚',
    color:         '',
    image,
    url:           getIQDeepUrl(path),
    originalUrl:   getIQDeepUrl(path),
    available:     true,
    rating:        4.6,
    deliveryDays:  '2-4 j',
    sizes:         resolvedCat === 'Chaussures'
                     ? (gender === 'M' ? ['40','41','42','43','44','45'] : ['36','37','38','39','40','41'])
                     : ['XS','S','M','L','XL'],
    isRealProduct: true,
  };
}

const IQ_SOURCES = [
  { catId: 6,   cat: 'Chaussures', gender: 'F' },
  { catId: 5,   cat: 'Chaussures', gender: 'M' },
  { catId: 620, cat: 'Hauts',      gender: 'F' },
  { catId: 619, cat: 'Hauts',      gender: 'M' },
];

async function fetchIQueens(gender) {
  const sources = IQ_SOURCES.filter(s => s.gender === gender);
  const all     = [];
  const seen    = new Set();

  await Promise.all(sources.map(async ({ catId, cat, gender: g }) => {
    try {
      // PrestaShop AJAX — plusieurs endpoints possibles
      // Format 1: module ps_facetedsearch
      // Format 2: controller category avec paramètres
      const urls = [
        `${IQ_BASE}/fr/${catId}?id_category=${catId}&n=48&from_xhr=1`,
        `${IQ_BASE}/fr/${catId}?controller=category&id_category=${catId}&format=json`,
        `${IQ_BASE}/module/ps_facetedsearch/search?id_category=${catId}&n=48`,
      ];
      
      let items = [];
      for (const url of urls) {
        try {
          const res = await fetch(url, {
            headers: {
              'Accept': 'application/json, text/javascript, */*',
              'X-Requested-With': 'XMLHttpRequest',
              'User-Agent': 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0)',
              'Referer': `${IQ_BASE}/fr/`,
            },
          });
          if (!res.ok) continue;
          const contentType = res.headers.get('content-type') || '';
          if (!contentType.includes('json')) continue;
          const data = await res.json();
          const found = data.products || data.product_list || data.rendered_products || [];
          if (found.length > 0) { items = found; break; }
        } catch { continue; }
      }

      for (const item of items) {
        const p = mapIQProduct(item, cat, g);
        if (p && !seen.has(p.id)) { seen.add(p.id); all.push(p); }
      }
    } catch(e) {
      console.warn('[IQ AJAX]', catId, e.message);
    }
  }));

  return all;
}

// Verrou global pour éviter les fetches parallèles depuis plusieurs écrans
const _fetchInFlight = new Map();

// ─── Fonction principale ──────────────────────────────────────
export async function fetchDynamicCatalog(gender, onProgress) {
  gender = gender || 'F';
  const cacheKey = `${CACHE_KEY}_${gender}`;

  // v1.13.30 — Stale-while-revalidate : on lit le cache MÊME périmé.
  const { data: cachedData, fresh } = await getCachedStale(cacheKey);
  // Cache frais → retour direct.
  if (cachedData?.length > 0 && fresh) {
    console.log(`[DYNAMIC] Cache frais: ${cachedData.length} produits`);
    if (onProgress) onProgress(100);
    return cachedData;
  }
  // Un fetch est déjà en cours : si on a du cache (même périmé) on l'affiche
  // tout de suite ; sinon on attend ce fetch.
  if (_fetchInFlight.has(gender)) {
    if (cachedData?.length > 0) { if (onProgress) onProgress(100); return cachedData; }
    return _fetchInFlight.get(gender);
  }

  const promise = (async () => {
    if (onProgress) onProgress(10);
    console.log('[DYNAMIC] Fetching real products...');

    // Cashmere : scraping live du site + CJ (multi-sources pour fiabilité)
    // iQueens : CJ API (multi-catégorie)
    // ═══ v1.8.11 : ajout TOUS depuis Lambda DB (cron sync chaque nuit) ═══
    // ═══ v1.10.21 : ajout iQueens depuis Lambda DB (cron sync chaque nuit) ═══
    //   Le cron `outfit-iqueens-catalog-cron` (2h UTC) scrape + insert dans
    //   partner_products. Le cron `outfit-catalog-analysis-cron` (4h UTC)
    //   analyse ces produits avec Claude Vision.
    //   Frontend lit donc directement depuis DB, pas besoin de scraper.
    //   On garde getCJIQueensProducts en parallèle comme fallback (si cron pas encore tourné).
    // v1.10.66 : iqueens entièrement servi depuis la DB Lambda (qui a le gender
    // corrigé via syncGenderOnly). On désactive l'appel CJ direct car il :
    //   - Récupère des produits non présents en DB (donc gender pas corrigé)
    //   - Cause des erreurs F/M dans l'app (mauvaise détection côté frontend)
    //   - Fait double appel inutile (la DB Lambda a déjà tout)
    // v1.13.32 — Les sources LIVE (scraping Cashmere + CJ) sont lentes et ne
    // doivent PAS bloquer l'affichage du catalogue. Les sources DB (TOUS +
    // iQueens, l'essentiel) sont rapides. On plafonne les sources live à 5s :
    // si elles ne répondent pas à temps → on affiche le catalogue DB tout de
    // suite (plus de "catalogue vide au 1er login"), elles rejoindront au prochain
    // chargement (cache) ou si elles répondent dans les temps.
    const withTimeout = (p, ms, fb) => Promise.race([
      Promise.resolve(p),
      new Promise((res) => setTimeout(() => res(fb), ms)),
    ]);
    const [cbResult, iqResult, cbCjResult, tousResult, iqDbResult] = await Promise.allSettled([
      withTimeout(fetchLiveCashmereProducts().catch(() => null), 5000, null),
      Promise.resolve([]),  // ← getCJIQueensProducts désactivé (utilise iqDbResult à la place)
      withTimeout(getCJCashmereProducts(gender).catch(() => []), 5000, []),  // Source secondaire CJ (cap 5s)
      // TOUS depuis Lambda /catalog?partner=tous (table partner_products,
      // alimentée par le cron outfit-tous-catalog-cron tous les jours à 3h UTC).
      // limit=500 → on récupère tout le stock disponible, le filtrage gender/qualité
      // se fait côté Lambda (gender) puis après la fusion (qualité).
      getCatalog({ partner: 'tous', limit: 1500, available: true, gender }).catch(() => []),
      // v1.10.21 : iQueens depuis DB (analysés par Claude Vision côté serveur)
      // v1.10.66 : maintenant SEULE source pour iqueens (gender corrigé via syncGenderOnly)
      getCatalog({ partner: 'iqueens', limit: 2000, available: true, gender }).catch(() => []),
    ]);

  if (onProgress) onProgress(90);

  let cbItemsRaw = cbResult.status === 'fulfilled' ? cbResult.value : null;
  const iqCjItems  = iqResult.status === 'fulfilled' ? (iqResult.value || []) : [];
  const cbCjItems  = cbCjResult.status === 'fulfilled' ? (cbCjResult.value || []) : [];
  // ═══ v1.8.11 : items TOUS récupérés depuis Lambda DB ═══
  const tousItemsRaw = tousResult.status === 'fulfilled' ? (tousResult.value || []) : [];
  console.log(`[DYNAMIC] TOUS depuis DB: ${tousItemsRaw.length} produits`);
  // ═══ v1.10.21 : items iQueens depuis DB (analysés Claude Vision côté serveur) ═══
  const iqDbItems = iqDbResult.status === 'fulfilled' ? (iqDbResult.value || []) : [];
  console.log(`[DYNAMIC] iQueens depuis DB: ${iqDbItems.length} produits (CJ: ${iqCjItems.length})`);

  // Pas de fallback statique : uniquement données CJ live + scraping live
  if (!Array.isArray(cbItemsRaw)) cbItemsRaw = [];

  // Ajouter les produits Cashmere via CJ (source principale maintenant)
  if (cbCjItems.length > 0) {
    console.log(`[DYNAMIC] +${cbCjItems.length} produits Cashmere via CJ (live)`);
    cbItemsRaw = [...cbItemsRaw, ...cbCjItems];
  }

  // Filtrer Cashmere par genre (live scraping retourne tout)
  // Harmonisation : 'M' (app) = 'H' (Cashmere). '*' = unisexe (toujours affiché)
  const normalizeGender = (g) => g === 'M' ? 'H' : g;
  const targetGender = normalizeGender(gender);
  const cbItems = cbItemsRaw.filter(p => {
    const pGender = normalizeGender(p.gender);
    return pGender === targetGender || pGender === '*';
  });

  // iQueens : seulement CJ (plus de fallback statique)
  // v1.10.21 : iQueens = DB (priorité, analysé par Claude Vision côté serveur)
  // + CJ (fallback pour les produits pas encore en DB ou en attendant le 1er run du cron)
  // Dédup par id : si un id existe en DB, on le préfère au CJ (DB a ai_metadata)
  const iqItems = (() => {
    const dbIds = new Set(iqDbItems.map(p => p.id));
    const cjOnly = iqCjItems.filter(p => !dbIds.has(p.id));
    return [...iqDbItems, ...cjOnly];
  })();

  // ═══ v1.8.11 : Préparation des items TOUS pour fusion ═══
  // TOUS est déjà filtré par genre côté Lambda (via ai_metadata→gender).
  // On pose juste partner='tous' au cas où c'est manquant.
  // ═══ v1.8.14 : marquer une sélection comme trending pour qu'ils ═══
  //   apparaissent dans la section "Tendances" de Home / Looks / etc.
  //   Stratégie :
  //     - Article avec discount > 0% → trending (mise en avant promo)
  //     - Sinon : hash de l'id pour échantillonner ~30% du catalogue (variété)
  //     - On ajoute aussi un trendTag basé sur la catégorie (Bijoux/Sacs)
  const tousFinal = tousItemsRaw.map(p => {
    const discount = parseFloat(p.discount) || 0;
    const idStr = String(p.id || '');
    // Hash simple pour échantillonner ~30% (mod 10 < 3) → ~150 produits sur 494
    const idHash = idStr.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    const isTrendySample = (idHash % 10) < 3;
    const shouldBeTrending = discount > 0 || isTrendySample;
    return {
      ...p,
      partner: p.partner || 'tous',
      brand:   p.brand   || 'TOUS',
      // ai_metadata virtuel pour que TrendingService.matchProductsToTrends
      // détecte ces articles comme trending
      ai_metadata: shouldBeTrending ? {
        ...(p.ai_metadata || {}),
        isTrendy: true,
        trendScore: discount > 0 ? 80 : 60,
        trendTag: p.category === 'Bijoux' ? 'bijoux iconiques'
                : p.category === 'Sacs'   ? 'maroquinerie chic'
                :                            'accessoires tendance',
        trendReason: discount > 0 ? `Promo -${Math.round(discount)}%` : 'Pièce signature TOUS',
      } : (p.ai_metadata || null),
      // Direct flags pour les composants qui lisent isTrending sans passer par enrichProductsWithTrends
      isTrending: shouldBeTrending,
      trendScore: shouldBeTrending ? (discount > 0 ? 80 : 60) : 0,
    };
  });

  console.log(`[DYNAMIC] CB: ${cbItems.length}, IQ: ${iqItems.length}, TOUS: ${tousFinal.length}`);

  const cbFinal = cbItems;
  const iqFinal = iqItems;

  // ──── Filtrage strict : image valide + lien de redirection ────
  // Un produit est gardé seulement si :
  //  - il a une image non vide (https:// ou http://)
  //  - il a un lien de redirection utilisable (affiliateUrl, originalUrl ou url)
  //  - le prix est > 0 (évite les stubs/placeholders)
  const isValidProduct = (p) => {
    if (!p) return false;
    const img = p.image || p.image_url;
    if (!img || typeof img !== 'string' || !/^https?:\/\//.test(img)) return false;
    // v1.13.26 — Exclure les partenaires masqués (Zalando/H&M/Mango).
    try {
      const { isBlockedPartnerImage } = require('../utils/imageFilter');
      if (isBlockedPartnerImage(img)) return false;
    } catch {}
    const link = p.affiliateUrl || p.originalUrl || p.url;
    if (!link || typeof link !== 'string' || !/^https?:\/\//.test(link)) return false;
    if (typeof p.price !== 'number' && typeof p.price !== 'string') return false;
    const priceNum = parseFloat(p.price);
    if (isNaN(priceNum) || priceNum <= 0) return false;
    return true;
  };

  // Dédoublonnage GLOBAL par ID + filtrage strict
  // ═══ v1.8.11 : TOUS inclus dans la fusion ═══
  const allRaw = [...cbFinal, ...iqFinal, ...tousFinal];
  const seenIds = new Set();
  const all = [];
  let rejected = 0;
  for (const p of allRaw) {
    if (!isValidProduct(p)) { rejected++; continue; }
    const pid = p.id || `auto_${p.partner || 'x'}_${p.name || 'x'}_${p.price || 0}`.replace(/\s+/g, '_');
    if (seenIds.has(pid)) continue;
    seenIds.add(pid);
    all.push(p.id ? p : { ...p, id: pid });
  }
  console.log(`[DYNAMIC] Total uniques: ${all.length} (rejetés invalides: ${rejected}, dédupliqués: ${allRaw.length - all.length - rejected})`);

    // ═══ FILTRE GENRE — sécurité légère sur les mismatches évidents ═══
    // L'API source (iQueens, Cashmere, etc.) retourne déjà des produits filtrés
    // par genre (URL women/ vs men/). On NE FAIT PAS de filtre IA derrière car
    // Claude Vision peut se tromper sur des coupes ambiguës. On garde juste la
    // regex texte pour rattraper les rares erreurs API (ex: "T-shirt homme" qui
    // se serait glissé dans une catégorie women/).
    let result = all;
    try {
      const GF = require('./GenderFilter');
      // Version regex legacy uniquement (filterHautsByGenderStrict, PAS la version AI)
      if (GF && GF.filterHautsByGenderStrict) {
        const beforeG = result.length;
        result = GF.filterHautsByGenderStrict(result, gender);
        console.log(`[DYNAMIC] gender filter (regex Hauts strict): ${result.length}/${beforeG}`);
      }
    } catch(e) { /* fail open */ }

    // ═══ FILTRE PRÉFÉRENCES DU JOUR (cold start popup) ═══
    // Si l'user a coché des catégories spécifiques (Sacs, Chaussures, etc.),
    // on met EN PREMIER les articles de ces catégories. On ne supprime pas
    // les autres pour ne pas vider le catalogue : on les remonte juste.
    // Idem pour le budget : tri qui priorise les articles dans la fourchette.
    try {
      const { getCachedPrefs } = require('./DailyPreferencesService');
      const prefs = getCachedPrefs();
      if (prefs && (prefs.categories?.length > 0 || prefs.budget)) {
        result = applyDailyPrefsSort(result, prefs);
        console.log(`[DYNAMIC] daily prefs applied: cats=${prefs.categories?.length || 0}, budget=${prefs.budget || 'none'}`);
      }
    } catch(e) { /* fail open */ }

    // ═══ FIX 2026-04 : dédup + agrégation tailles avant cache ═══
    // À chaque arrivage de nouveaux articles dans le catalogue dynamique,
    // on applique mergeDuplicateProducts pour :
    //  - fusionner les variantes de tailles (1 article au lieu de 5)
    //  - agréger les tailles dans le champ `sizes` (déduplique si présent)
    //  - trier intelligemment les tailles (XS→S→M→L→XL→XXL puis numérique)
    // Ainsi tous les écrans qui consomment fetchDynamicCatalog (Home, Looks,
    // Catalog, Wardrobe, Travel) reçoivent des données déjà propres.
    try {
      const { mergeDuplicateProducts } = require('../utils/mergeProducts');
      const before = result.length;
      result = mergeDuplicateProducts(result);
      if (before !== result.length) {
        console.log(`[DYNAMIC] merge variantes : ${before} → ${result.length} (tailles agrégées)`);
      }
    } catch(e) { console.warn('[DYNAMIC] mergeDuplicateProducts failed:', e.message); }

    if (result.length > 0) await setCache(cacheKey, result);
    if (onProgress) onProgress(100);
    return result;
  })();

  _fetchInFlight.set(gender, promise);
  promise.finally(() => _fetchInFlight.delete(gender));

  // v1.13.30 — SWR : si on a du cache (périmé), affichage INSTANTANÉ et le fetch
  // tourne en arrière-plan (il fait setCache → frais au prochain affichage).
  // Sinon (aucun cache = tout 1er chargement), on attend le fetch.
  if (cachedData?.length > 0) {
    if (onProgress) onProgress(100);
    promise.catch(() => {});
    return cachedData;
  }
  return promise;
}

// ─── Ping impression CJ ───────────────────────────────────────
// Format CJ image pixel : https://www.lduhtrp.net/image-PUB-LINK
// CJ exige des impressions régulières pour valider le tracking des clics.
export function pingCJImpression(linkId = '10471851') {
  const url = `https://www.lduhtrp.net/image-${CJ_PUB}-${linkId}`;
  fetch(url, { method: 'GET', mode: 'no-cors' }).catch(() => {});
}

export function pingAllCJImpressions() {
  pingCJImpression('10471851'); // Cashmere Boutique
  pingCJImpression('15830678'); // iQueens
}
